// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
// Presentation only. Events keep their original simulation step; no effect can score or move a ball.
import type { MachineDefinition } from '@core/machine'
import {
  EV_BALL_CAPTURED, EV_BALL_EJECTED, EV_BANK_COMPLETE, EV_BUMPER_HIT,
  EV_SLINGSHOT_FIRED, EV_SPINNER_REVOLUTION, EV_SWITCH_CLOSED, EV_TARGET_DOWN,
} from '@core/sim/events'
import { lightingProfileFor, lightingRgb, type LightingProfile, type LightingShow } from './lightingProfiles'
import {componentStandby} from './componentStandby'

export const LIGHTING_TIMING = { bumper: 220, sling: 180, target: 240, kicker: 320, spinner: 550, chase: 700,
  bank: 900, combo: 900, jackpot: 1200, multiball: 1600 } as const
export const MAX_LIGHTING_COMPONENTS = 256
export interface LightingContext { readonly step: number; readonly attract: boolean; readonly tilted: boolean;
  readonly balls: number; readonly mode: string }
interface Activity { kind: string; at: number; energy: number; from: number; held: boolean; chase: number; holdUntil: number; ejectedAt:number; ejectedSpeed:number }
export interface LightingSample { level: number; red: number; green: number; blue: number; activity: number; travel: number }
export const createLightingSample = (): LightingSample => ({ level: 0, red: 0, green: 0, blue: 0, activity: 0, travel: -1 })
export function lightingEnvelope(age: number, duration: number, from = 0, reduced = false): number {
  if (age < 0) return 0
  const length = reduced ? Math.max(600, duration) : duration
  if (age >= length) return 0
  const attack = reduced ? 100 : 12
  const decay = Math.max(0, 1 - Math.max(0, age - attack) / (length - attack))
  return (from + (1 - from) * Math.min(1, age / attack)) * decay * decay
}
export function lightingShowEnvelope(age: number, duration: number): number {
  if (age < 0 || age >= duration) return 0
  // Smooth, finite and never oscillating. Large-area effects cannot turn into strobes.
  const edge = Math.min(1, age / 180, (duration - age) / 300)
  return edge * edge * (3 - 2 * edge)
}
const PRIORITY: Record<LightingShow, number> = { combo: 1, bank: 2, multiball: 3, jackpot: 4 }

export class ComponentLightingState {
  readonly profile: LightingProfile
  readonly components = new Map<string, Activity>()
  readonly lampOwners = new Map<string, string>()
  private readonly banks = new Map<string, readonly string[]>()
  private lastStep = -1
  private lastBalls = 0
  private show: LightingShow = 'bank'
  private showAt = -1
  private mode = ''
  private attract = false
  private tilted = false
  private readonly idleRgb: readonly [number, number, number]
  private readonly hitRgb: readonly [number, number, number]
  private readonly awardRgb: readonly [number, number, number]
  private readonly activeRgb: readonly [number, number, number]
  private readonly modeColours = new Map<string, readonly [number, number, number]>()
  step = 0
  constructor(definition: Pick<MachineDefinition,'machineId'|'lamps'|'devices'|'layers'>) {
    this.profile = lightingProfileFor(definition.machineId, definition.lamps?.find(l => l.kind === 'insert')?.colour)
    this.idleRgb = lightingRgb(this.profile.palette.idle); this.hitRgb = lightingRgb(this.profile.palette.hit)
    this.awardRgb = lightingRgb(this.profile.palette.award); this.activeRgb = lightingRgb(this.profile.palette.active)
    for (const [id, hex] of Object.entries(this.profile.modes)) this.modeColours.set(id, lightingRgb(hex))
    for (const d of definition.devices) {
      this.add(d.id, d.kind)
      if (d.lamp !== undefined) this.lampOwners.set(d.lamp, d.id)
      if (d.kind === 'dropTargetBank') this.banks.set(d.id, d.targets)
    }
    for (const l of definition.layers) if (l.kind === 'ramp' || l.kind === 'wire') this.add(l.id, 'path')
  }
  private add(id: string, kind: string): void {
    if (this.components.size < MAX_LIGHTING_COMPONENTS) this.components.set(id, { kind, at: -1, energy: 0, from: 0, held: false, chase: -1, holdUntil: -1, ejectedAt:-1, ejectedSpeed:0 })
  }
  reset(): void {
    for (const c of this.components.values()) { c.at = c.chase = c.holdUntil = c.ejectedAt = -1; c.energy = c.from = c.ejectedSpeed = 0; c.held = false }
    this.showAt = this.lastStep = -1; this.lastBalls = 0; this.step = 0
    this.mode = ''; this.attract = this.tilted = false
  }
  advance(context: LightingContext): void {
    if (context.step < this.lastStep) this.reset()
    this.step = this.lastStep = context.step
    this.attract = context.attract; this.tilted = context.tilted; this.mode = context.mode
    if (context.balls > 1 && this.lastBalls <= 1 && !context.attract) this.signal('multiball', context.step)
    this.lastBalls = context.balls
    if (this.attract || this.tilted) this.showAt = -1
  }
  prime(context: LightingContext): void { this.reset(); this.lastBalls = context.balls; this.advance(context) }
  cue(id: string, step: number): void {
    const show = Object.hasOwn(this.profile.cues, id) ? this.profile.cues[id] : undefined
    if (show !== undefined) this.signal(show, step)
  }
  signal(show: LightingShow, step: number): void {
    if (this.showAt >= 0 && step >= this.showAt && step - this.showAt < LIGHTING_TIMING[this.show]) {
      if (PRIORITY[show] < PRIORITY[this.show] || show === this.show) return
    }
    this.show = show; this.showAt = step
  }
  event(type: number, id: string, step: number, value = 1): void {
    if (!Number.isFinite(step) || step < 0) return
    if (type === EV_BANK_COMPLETE) {
      let index = 0
      for (const target of this.banks.get(id) ?? []) {
        const targetState = this.components.get(target)
        if (targetState !== undefined) targetState.chase = step + index++ * 120
      }
      this.signal('bank', step); return
    }
    const c = this.components.get(id)
    if (c === undefined) return
    if (type === EV_BALL_CAPTURED) c.held = true
    if (type === EV_BALL_EJECTED) { c.held = false; c.chase = step; c.ejectedAt=step; c.ejectedSpeed=value }
    if (type === EV_SPINNER_REVOLUTION) {
      c.energy = Math.min(1, this.activity(c, step, false) + 0.28); c.at = step; return
    }
    if (type === EV_BUMPER_HIT || type === EV_SLINGSHOT_FIRED || type === EV_TARGET_DOWN
      || type === EV_BALL_CAPTURED || type === EV_BALL_EJECTED
      || (type === EV_SWITCH_CLOSED && ['standupTarget', 'dropTarget', 'rollover', 'gate', 'captiveBall'].includes(c.kind))) {
      this.hit(id, step, Math.min(1, 0.7 + Math.abs(value) * 0.06))
    }
  }
  // The caller verifies forward exit on the simulation's sampled path before acknowledging success.
  pathCompleted(id: string, step: number): void { const c = this.components.get(id); if (c !== undefined) { c.chase = step; this.hit(id, step, 1) } }
  ejectedSpeed(id:string):number{return this.components.get(id)?.ejectedSpeed??0}
  ejectedAt(id:string):number{return this.components.get(id)?.ejectedAt??-1}
  held(id: string, held: boolean): void { const c = this.components.get(id); if (c !== undefined) c.held = held }
  private hit(id: string, step: number, energy: number): void {
    const c = this.components.get(id)
    if (c === undefined || step < c.at) return
    const previous = this.activity(c, step, false), dense = c.at >= 0 && step - c.at < 350
    c.energy = Math.min(1, Math.max(previous, energy)); c.from = c.energy > 0 ? previous / c.energy : 0
    c.holdUntil = dense ? step + 180 : -1; c.at = step
  }
  private duration(kind: string): number {
    return kind === 'popBumper' ? LIGHTING_TIMING.bumper : kind === 'slingshot' ? LIGHTING_TIMING.sling
      : kind === 'kicker' ? LIGHTING_TIMING.kicker : LIGHTING_TIMING.target
  }
  private activity(c: Activity, step: number, reduced: boolean): number {
    if (c.at < 0) return 0
    if (c.kind === 'spinner') return c.energy * Math.max(0, Math.min(1, 1 - (step - c.at) / LIGHTING_TIMING.spinner))
    const age = step - c.at, attack = reduced ? 100 : 12
    const held = Math.max(0, c.holdUntil - c.at - attack)
    const effectiveAge = age <= attack ? age : Math.max(attack, age - held)
    return c.energy * lightingEnvelope(effectiveAge, this.duration(c.kind), c.from, reduced)
  }
  showLevel(reduced = false): number {
    if (this.attract || this.tilted || this.showAt < 0) return 0
    return lightingShowEnvelope(this.step - this.showAt, LIGHTING_TIMING[this.show]) * (reduced ? 0.18 : 0.65)
  }
  modeColour(): readonly [number, number, number] { return this.modeColours.get(this.mode) ?? this.activeRgb }
  get modeTint(): number { return this.mode === '' || this.attract || this.tilted ? 0 : 0.22 }
  sample(id: string, base: number, reduced: boolean, out: LightingSample, phase = 0): void {
    const c = this.components.get(id)
    const activity = c === undefined || this.tilted || this.attract ? 0 : this.activity(c, this.step, reduced) * (reduced ? 0.3 : 1)
    const show = this.showLevel(reduced)
    const age = c === undefined || c.chase < 0 ? -1 : this.step - c.chase
    const travel = age >= 0 && age < LIGHTING_TIMING.chase ? age / LIGHTING_TIMING.chase : -1
    const chase = travel < 0 ? 0 : Math.max(0, 1 - Math.abs(phase - travel) / 0.22) * (reduced ? 0.2 : 0.7)
    // Internal bumper and sling lamps stay softly on between strikes; activity still follows hits.
    const standby = componentStandby(c?.kind)
    const idle = this.attract ? standby + 0.02 + 0.10 * (0.5 - 0.5 * Math.cos(2 * Math.PI * (this.step / 3000 - phase))) : standby
    const held = c?.held && !this.tilted ? 0.50 : 0
    out.activity = activity + chase * 0.6; out.travel = travel
    out.level = this.tilted ? 0.08 : Math.min(1, Math.max(idle, base * 0.62, held) + activity * 0.65 + chase + show * 0.35)
    const activeMix = Math.min(0.75, Math.max(base, held, this.modeTint)), hitMix = Math.min(0.55, activity * 0.55)
    const awardMix = Math.min(0.6, show + chase * 0.4)
    out.red = this.channel(0, activeMix, hitMix, awardMix)
    out.green = this.channel(1, activeMix, hitMix, awardMix)
    out.blue = this.channel(2, activeMix, hitMix, awardMix)
  }
  private channel(channel: 0 | 1 | 2, active: number, hit: number, award: number): number {
    let v = this.idleRgb[channel] * (1 - active) + this.modeColour()[channel] * active
    v = v * (1 - hit) + this.hitRgb[channel] * hit
    return v * (1 - award) + this.awardRgb[channel] * award
  }
}
